import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

// Owner-safe error formatter (inline — no cross-module dependency so the component is testable with a REAL injected
// api client and no module mocking). Surfaces the backend's safe code/message, never a raw response or stack.
const commercialError = (e) => {
  const d = e && e.response && e.response.data;
  return (d && (d.detail?.message || d.detail?.code || d.message)) || (e && e.message) || 'Request failed.';
};

// GOV13 — SuperAdmin (owner-plane) queue for Enterprise / on-request COMMERCIAL_REVIEW requests. Lives ONLY in the
// SuperAdmin Web application and calls ONLY the owner route (/api/v1/super-admin/commercial/review-requests) with the
// owner session (the `api` client is the SuperAdmin useOwnerApi). It never renders Business controls and never calls a
// Business route. Owner may advance a request's status (audited server-side); it NEVER assigns Enterprise, creates a
// checkout, or changes a tenant's plan.
const NEXT_STATUS = {
  SUBMITTED: ['UNDER_REVIEW', 'CONTACTED', 'APPROVED', 'DECLINED', 'CANCELLED'],
  UNDER_REVIEW: ['CONTACTED', 'APPROVED', 'DECLINED', 'CANCELLED'],
  CONTACTED: ['APPROVED', 'DECLINED', 'CANCELLED'],
  APPROVED: [], DECLINED: [], CANCELLED: [],
};
const TONE = {
  SUBMITTED: 'bg-blue-100 text-blue-800', UNDER_REVIEW: 'bg-amber-100 text-amber-800',
  CONTACTED: 'bg-purple-100 text-purple-800', APPROVED: 'bg-green-100 text-green-800',
  DECLINED: 'bg-red-100 text-red-700', CANCELLED: 'bg-gray-100 text-gray-600',
};

export default function CommercialReviewRequests({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/api/v1/super-admin/commercial/review-requests');
      setRows((r.data && r.data.requests) || []);
    } catch (e) {
      setError(commercialError(e));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const changeStatus = async (req, status) => {
    if (!status || busyId) return;
    setBusyId(req.request_id); setToast(null);
    try {
      await api.patch(`/api/v1/super-admin/commercial/review-requests/${encodeURIComponent(req.request_id)}`, { status });
      setToast({ ok: true, msg: `Request ${req.request_id} → ${status}. Audit recorded.` });
      await load();
    } catch (e) {
      setToast({ ok: false, msg: commercialError(e) });
    } finally { setBusyId(null); }
  };

  return (
    <Card className="bg-white shadow-md" data-testid="review-requests-panel">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Commercial review requests</CardTitle>
        <Button onClick={load} variant="outline" size="sm" disabled={loading}>Refresh</Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {toast && (
          <div className={`mb-3 text-sm p-2 rounded ${toast.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`} role="status">{toast.msg}</div>
        )}
        {error && <p className="text-sm text-red-600" role="alert" data-testid="review-requests-error">{error}</p>}
        {loading ? <p className="text-sm text-gray-400">Loading…</p>
          : rows.length === 0 ? <p className="text-sm text-gray-500" data-testid="review-requests-empty">No commercial review requests yet.</p>
            : (
              <table className="w-full text-sm" data-testid="review-requests-table">
                <thead className="bg-gray-50 border-b"><tr>
                  {['Tenant', 'Product / Version', 'Contact', 'Submitted', 'Message', 'Status', 'Advance'].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>))}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.request_id} className="hover:bg-gray-50" data-testid={`review-request-${r.request_id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{r.business_id}</td>
                      <td className="px-3 py-2">{r.product_key} / {r.product_version_id}</td>
                      <td className="px-3 py-2">
                        <div className="text-gray-900">{r.contact_name}</div>
                        <div className="text-xs text-gray-500">{r.contact_email}{r.contact_phone ? ` · ${r.contact_phone}` : ''}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{String(r.requested_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-3 py-2 text-xs max-w-[16rem] truncate" title={r.message || ''}>{r.message || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TONE[r.status] || 'bg-gray-100 text-gray-600'}`}
                          data-testid={`review-request-${r.request_id}-status`}>{r.status}</span>
                      </td>
                      <td className="px-3 py-2">
                        {(NEXT_STATUS[r.status] || []).length > 0 ? (
                          <select className="border rounded px-2 py-1 text-xs" disabled={busyId === r.request_id}
                            defaultValue="" onChange={(e) => changeStatus(r, e.target.value)}
                            data-testid={`review-request-${r.request_id}-advance`}
                            aria-label={`Advance status for ${r.request_id}`}>
                            <option value="" disabled>Advance…</option>
                            {NEXT_STATUS[r.status].map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
      </CardContent>
    </Card>
  );
}
